/** Small presentational building blocks shared by all pages. */
import { useCallback, useEffect, useRef, useState, useSyncExternalStore, type ReactNode } from "react";
import { ApiError, ApiSchemaError } from "@/api/client";
import type { TermKey } from "@/lib/terms";
import { Term } from "./Term";

export function Card({
  title,
  actions,
  children,
  className = "",
  id,
}: {
  title?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  className?: string;
  id?: string;
}) {
  return (
    <section className={`card ${className}`} id={id}>
      {(title || actions) && (
        <header>
          {typeof title === "string" ? <h2>{title}</h2> : title}
          {actions && <div className="row">{actions}</div>}
        </header>
      )}
      {children}
    </section>
  );
}

export function Tile({
  label,
  value,
  sub,
  bar,
  testId,
  term,
}: {
  label: string;
  value: ReactNode;
  sub?: ReactNode;
  bar?: number | null;
  testId?: string;
  /** a training word: the label can be tapped for its plain explanation */
  term?: TermKey;
}) {
  return (
    <div className="tile" data-testid={testId}>
      <div className="label">{term ? <Term k={term}>{label}</Term> : label}</div>
      <div className="value">{value}</div>
      {sub && <div className="sub">{sub}</div>}
      {bar != null && (
        <div className="bar" aria-hidden>
          <span style={{ width: `${Math.max(0, Math.min(100, bar * 100))}%` }} />
        </div>
      )}
    </div>
  );
}

export function Badge({
  children,
  tone = "",
}: {
  children: ReactNode;
  tone?: "" | "accent" | "good" | "warn" | "bad" | "info";
}) {
  return <span className={`badge ${tone}`}>{children}</span>;
}

export function Empty({ children }: { children: ReactNode }) {
  return <div className="empty">{children}</div>;
}

export function Skeleton({ height = 120 }: { height?: number }) {
  return (
    <div
      className="skeleton"
      style={{ minHeight: height }}
      role="status"
      aria-busy="true"
      aria-label="Loading"
    />
  );
}

/** A list that is loading: grey rows shaped like the rows that come (BR-136). */
export function SkeletonRows({ rows = 6 }: { rows?: number }) {
  return (
    <div className="skeleton-rows" role="status" aria-busy="true" aria-label="Loading">
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="skeleton" data-testid="skeleton-row" />
      ))}
    </div>
  );
}

/** A failed load in one plain sentence: the server's own text (often technical) is never shown; the error id is,
 * so a rider can quote it in "Report a problem" (BR-131). */
export function loadErrorText(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.status === 0) return `Couldn't load this. ${error.message}`; // our own words: offline or no answer
    if (error.status === 404) return "Couldn't load this: it wasn't found. It may have been removed.";
    if (error.status === 403) return "Couldn't load this: your account has no access to it.";
    if (error.status === 429)
      return "Couldn't load this: too many requests just now. Wait a minute and try again.";
    if (error.status >= 400 && error.status < 500)
      // our request wasn't accepted: not a problem on the server's side
      return "Couldn't load this: the request wasn't accepted. Reload the page and try again.";
    const id = error.errorId ? ` (error ${error.errorId})` : "";
    return `Couldn't load this: Bason had a problem on its side${id}. Try again in a moment.`;
  }
  return "Couldn't load this. Try again in a moment.";
}

/** A failed load, said plainly, with a way out (G3): "Try again" refetches; an answer the app doesn't understand
 * (usually this tab still runs the previous version after a release) offers a reload instead. */
export function ErrorBox({ error, retry }: { error: unknown; retry?: () => unknown }) {
  if (error instanceof ApiSchemaError) {
    return (
      <div className="empty stack" role="alert" style={{ gap: 8 }}>
        <span>Bason was updated. Reload the page to continue.</span>
        <div>
          <button onClick={() => window.location.reload()}>Reload</button>
        </div>
      </div>
    );
  }
  return (
    <div className="empty stack" role="alert" style={{ gap: 8 }}>
      <span>{loadErrorText(error)}</span>
      {retry && (
        <div>
          <button onClick={() => void retry()}>Try again</button>
        </div>
      )}
    </div>
  );
}

/** Native <dialog> wrapper (accessible, Esc closes). */
export function Dialog({
  open,
  onClose,
  title,
  children,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (open && !d.open) {
      if (typeof d.showModal === "function") d.showModal();
      else d.setAttribute("open", "");
    } else if (!open && d.open) {
      if (typeof d.close === "function") d.close();
      else d.removeAttribute("open");
    }
  }, [open]);
  return (
    <dialog ref={ref} onClose={onClose} aria-label={title}>
      <div className="row between" style={{ marginBottom: 12 }}>
        <h2>{title}</h2>
        <button className="ghost small" onClick={onClose} aria-label="Close">
          ✕
        </button>
      </div>
      {open && children}
    </dialog>
  );
}

/** Accessible tabs (roving focus with arrow keys); the selected tab is kept in the URL hash. */
export function Tabs<T extends string>({
  tabs,
  value,
  onChange,
  label,
}: {
  tabs: readonly { id: T; label: string }[];
  value: T;
  onChange: (id: T) => void;
  label: string;
}) {
  const row = useRef<HTMLDivElement>(null);
  const [overflowing, setOverflowing] = useState(false);
  useEffect(() => {
    const el = row.current;
    if (!el) return;
    const check = () => setOverflowing(el.scrollWidth > el.clientWidth + 1);
    check();
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(check) : null;
    ro?.observe(el);
    return () => ro?.disconnect();
  }, [tabs.length]);
  useEffect(() => {
    // the selected tab is never hidden behind the fade on a phone
    const sel = row.current?.querySelector<HTMLElement>('[aria-selected="true"]');
    if (sel && typeof sel.scrollIntoView === "function")
      sel.scrollIntoView({ block: "nearest", inline: "nearest" });
  }, [value]);
  return (
    <div ref={row} className={`tabs${overflowing ? " overflowing" : ""}`} role="tablist" aria-label={label}>
      {tabs.map((t, i) => (
        <button
          key={t.id}
          role="tab"
          type="button"
          id={`tab-${t.id}`}
          aria-selected={t.id === value}
          aria-controls={`panel-${t.id}`}
          tabIndex={t.id === value ? 0 : -1}
          onClick={() => onChange(t.id)}
          onKeyDown={(e) => {
            const d = e.key === "ArrowRight" ? 1 : e.key === "ArrowLeft" ? -1 : 0;
            if (!d) return;
            const next = tabs[(i + d + tabs.length) % tabs.length]!;
            onChange(next.id);
            document.getElementById(`tab-${next.id}`)?.focus();
          }}
        >
          {t.label}
        </button>
      ))}
    </div>
  );
}

/** Tab state that survives reloads via the URL hash (#tab). */
export function useHashTab<T extends string>(ids: readonly T[], fallback: T): [T, (t: T) => void] {
  const read = () => {
    const h = (typeof window !== "undefined" ? window.location.hash.slice(1) : "") as T;
    return ids.includes(h) ? h : fallback;
  };
  const [tab, setTab] = useState<T>(read);
  // a link to another tab of the page you are on ("/plan#events" from the coach) changes the hash without a reload
  useEffect(() => {
    const onHash = () => setTab(read());
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ids.join("|"), fallback]);
  const set = (t: T) => {
    setTab(t);
    try {
      window.history.replaceState(null, "", `#${t}`);
    } catch {
      /* sandboxed previews */
    }
  };
  return [tab, set];
}

/** Whether a CSS media query matches now (and re-renders when that changes), e.g. phones: "(max-width: 899px)". */
export function useMediaQuery(query: string): boolean {
  // one subscription per query, not per render; addListener for older Safari (review of G4b)
  const subscribe = useCallback(
    (fn: () => void) => {
      const m = window.matchMedia(query);
      if (m.addEventListener) m.addEventListener("change", fn);
      else m.addListener(fn);
      return () => (m.removeEventListener ? m.removeEventListener("change", fn) : m.removeListener(fn));
    },
    [query],
  );
  return useSyncExternalStore(
    subscribe,
    () => window.matchMedia(query).matches,
    () => false,
  );
}

/** Ref callback that focuses an element once, when it appears (stable: never on re-renders). For what replaces
 * a control that had the focus, so the focus is handed on instead of falling back to the page (G3). */
export function focusOnMount(el: HTMLElement | null) {
  el?.focus();
}
